import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Container, Link, Paper, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

import { getFiled } from "../../services/backend/CorrespondenceService";
import FiledViewItem from "./FiledViewItem";
import FiledSummaryDetails from "./FiledSummaryDetails";
import FiledCancellationNotice from "./FiledCancellationNotice";
import RadicadoTimeline from "./RadicadoTimeline";

import { pqrsdText } from "../pqrsd/pqrsdLabel";
// Read-only page for an annulled radicado, whether it was annulled open or
// finalized: who annulled it, when and why, above the radicado itself.
export default function FiledCancelledView() {
  const { filedId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Left by a form that was open when someone else annulled the radicado.
  const notice = location.state?.notice || null;
  const user = useSelector((state) => state.user.value);
  const [filed, setFiled] = useState();
  // "not_found": the server answered that it doesn't exist. "unavailable": no
  // usable answer at all (offline, expired session), which is worth a retry.
  const [failure, setFailure] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setFailure(null);
    (async () => {
      const responseJSON = await getFiled(user?.token, filedId);
      if (!active) return;
      if (!responseJSON) {
        setFailure("unavailable");
        return;
      }
      if (responseJSON.result === false) {
        setFailure("not_found");
        return;
      }
      setFiled(responseJSON);
    })();
    return () => {
      active = false;
    };
  }, [user, filedId, attempt]);

  // The link may be stale: a radicado that isn't annulled has its own page.
  useEffect(() => {
    if (!filed || filed.status === "CANCELLED") return;
    if (filed.status === "DONE") navigate(`/filed/view/${filed.referenceId}`, { replace: true });
    else navigate(`/filed/${filed.filedId}`, { replace: true });
  }, [filed, navigate]);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ArrowBack />}
        sx={{ margin: { xs: 1, sm: 2 } }}
        onClick={() => navigate(-1)}
      >
        Volver
      </Button>
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
        {notice && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {notice}
          </Alert>
        )}
        {failure === "not_found" && (
          <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
            No se encontró el radicado {filedId}.
          </Typography>
        )}
        {failure === "unavailable" && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No se pudo cargar el radicado {filedId}. Revisa tu conexión o recarga la página.
            </Typography>
            <Button variant="outlined" onClick={() => setAttempt((n) => n + 1)}>
              Reintentar
            </Button>
          </Box>
        )}
        {!failure && !filed && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {filed && filed.status === "CANCELLED" && (
          <>
            <FiledCancellationNotice filed={filed} />
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Radicado
              </Typography>
              <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
                <FiledViewItem item={filed} />
              </Box>
              <FiledSummaryDetails item={filed} />
              {filed.filedType === "pqrsd" && (
                <Box sx={{ mt: 2 }}>
                  <Link component={RouterLink} to={`/pqrsd/admin/${encodeURIComponent(filed.filedId)}`}>
                    {pqrsdText("Ver PQRSD")}
                  </Link>
                </Box>
              )}
            </Paper>
            <Box sx={{ mb: { xs: 2, sm: 3 } }}>
              <RadicadoTimeline
                rootRadicadoId={filed.response?.rootRadicadoId || filed.filedId}
                currentRadicadoId={filed.filedId}
                currentRadicadoReferenceId={filed.referenceId}
                isResponseView={!!filed.response?.parentRadicadoId}
                originalRadicadoId={filed.response?.rootRadicadoId || filed.filedId}
              />
            </Box>
          </>
        )}
      </Container>
    </>
  );
}
